import { useCallback, useState } from "react";
import { ITestRunnerMethod, ITestRunnerProps, TestRunSide } from "./test-runner.types";

export const paramKey = (methodName: string, paramName: string): string => `${methodName}.${paramName}`;

// Scalar type names across the supported languages (Go / Java / Python) and the binding vocabulary, lowercased.
const STRING_TYPES: Set<string> = new Set(["string", "str", "char", "charsequence"]);
const BOOLEAN_TYPES: Set<string> = new Set(["bool", "boolean"]);
const NUMBER_TYPES: Set<string> = new Set([
  "int",
  "int8",
  "int16",
  "int32",
  "int64",
  "uint",
  "uint8",
  "uint16",
  "uint32",
  "uint64",
  "uintptr",
  "byte",
  "rune",
  "short",
  "long",
  "integer",
  "number",
  "decimal",
  "bigdecimal",
  "float",
  "float32",
  "float64",
  "double"
]);
const DATE_TYPES: Set<string> = new Set([
  "time.time",
  "datetime",
  "date",
  "instant",
  "localdate",
  "localdatetime",
  "zoneddatetime",
  "timestamp"
]);

const parseJsonOrRaw = (raw: string): unknown => {
  try {
    return JSON.parse(raw);
  } catch {
    return raw;
  }
};

/**
 * Coerces a raw text input to the JSON value the worker expects, based on the parameter's declared type, so that
 * a `string` parameter sends `"1"` (not the number `1`). Collections / maps / domain objects keep their JSON shape
 * (their box is pre-filled with a template); unknown types fall back to a best-effort JSON parse.
 */
const coerceArg = (type: string, raw: string): unknown => {
  const normalized: string = (type || "").trim().toLowerCase();
  if (
    normalized.includes("[]") ||
    normalized.includes("<") ||
    normalized.startsWith("[") ||
    normalized.startsWith("list") ||
    normalized.startsWith("array") ||
    normalized.startsWith("set") ||
    normalized.startsWith("map") ||
    normalized.startsWith("dict") ||
    normalized === "object"
  ) {
    return parseJsonOrRaw(raw);
  }

  const scalar: string = normalized.startsWith("*") ? normalized.slice(1) : normalized;
  if (STRING_TYPES.has(scalar)) return raw;
  if (DATE_TYPES.has(scalar)) return raw;
  if (BOOLEAN_TYPES.has(scalar)) return raw.trim() === "true";
  if (NUMBER_TYPES.has(scalar)) {
    const parsed: number = Number(raw);
    return Number.isNaN(parsed) ? 0 : parsed;
  }
  // Complex/domain type (struct / record / class / dataclass) or unknown — treat as JSON.
  return parseJsonOrRaw(raw);
};

const buildArgs = (method: ITestRunnerMethod, paramValues: Record<string, string>): unknown[] =>
  method.parameters.map((p) => coerceArg(p.type, paramValues[paramKey(method.name, p.name)] ?? ""));

/** Pre-fills complex/list params with their JSON template so the user edits the expected shape, not an empty box. */
const seedTemplates = (
  method: ITestRunnerMethod | undefined,
  values: Record<string, string>
): Record<string, string> => {
  if (!method) return values;
  const next: Record<string, string> = { ...values };
  method.parameters.forEach((p) => {
    const key: string = paramKey(method.name, p.name);
    if (p.template && !next[key]) {
      next[key] = p.template;
    }
  });
  return next;
};

const extractErrorMessage = (error: unknown): string =>
  (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
  (error as Error)?.message ||
  "Unknown error";

/**
 * State + execution logic for the Test Console, separated from rendering. Owns the open/parse/select/
 * run lifecycle; the parent supplies parsing and execution through props.
 */
export interface TestRunnerController {
  open: boolean;
  methods: ITestRunnerMethod[];
  selectedMethod: string;
  output: string;
  loading: boolean;
  running: boolean;
  runningSide: TestRunSide | null;
  toggleOpen: () => void;
  refresh: () => void;
  selectMethod: (name: string) => void;
  paramValue: (methodName: string, paramName: string) => string;
  setParam: (methodName: string, paramName: string, value: string) => void;
  run: (serverSide: boolean) => void;
}

export const useTestRunner = ({ onParseMethods, onExecute }: ITestRunnerProps): TestRunnerController => {
  const [open, setOpen] = useState<boolean>(false);
  const [methods, setMethods] = useState<ITestRunnerMethod[]>([]);
  const [selectedMethod, setSelectedMethod] = useState<string>("");
  const [paramValues, setParamValues] = useState<Record<string, string>>({});
  const [output, setOutput] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [runningSide, setRunningSide] = useState<TestRunSide | null>(null);

  const parse = useCallback(async (): Promise<void> => {
    setLoading(true);
    try {
      const parsed: ITestRunnerMethod[] = await onParseMethods();
      const first: ITestRunnerMethod | undefined = parsed.length > 0 ? parsed[0] : undefined;
      setMethods(parsed);
      setSelectedMethod(first?.name ?? "");
      setParamValues(seedTemplates(first, {}));
      setOutput("");
    } catch {
      setMethods([]);
      setOutput("Error: Failed to parse code");
    } finally {
      setLoading(false);
    }
  }, [onParseMethods]);

  const toggleOpen = useCallback((): void => {
    const next: boolean = !open;
    setOpen(next);
    if (next && methods.length === 0) parse();
  }, [open, methods.length, parse]);

  const selectMethod = useCallback(
    (name: string): void => {
      if (name === selectedMethod) return;
      setSelectedMethod(name);
      setOutput("");
      const method: ITestRunnerMethod | undefined = methods.find((m) => m.name === name);
      setParamValues((prev) => seedTemplates(method, prev));
    },
    [selectedMethod, methods]
  );

  const paramValue = useCallback(
    (methodName: string, paramName: string): string => paramValues[paramKey(methodName, paramName)] ?? "",
    [paramValues]
  );

  const setParam = useCallback((methodName: string, paramName: string, value: string): void => {
    setParamValues((prev) => ({ ...prev, [paramKey(methodName, paramName)]: value }));
  }, []);

  const run = useCallback(
    async (serverSide: boolean): Promise<void> => {
      const method: ITestRunnerMethod | undefined = methods.find((m) => m.name === selectedMethod);
      if (!method) return;
      setRunningSide(serverSide ? "server" : "client");
      setOutput("");
      try {
        const result: string = await onExecute(method.name, buildArgs(method, paramValues), serverSide);
        setOutput(result);
      } catch (error: unknown) {
        setOutput(`Error: ${extractErrorMessage(error)}`);
      } finally {
        setRunningSide(null);
      }
    },
    [methods, selectedMethod, paramValues, onExecute]
  );

  return {
    open,
    methods,
    selectedMethod,
    output,
    loading,
    running: runningSide !== null,
    runningSide,
    toggleOpen,
    refresh: parse,
    selectMethod,
    paramValue,
    setParam,
    run
  };
};
